import { Button } from '@src/shared/ui/components/Button';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { motion } from 'framer-motion';
import { isEmpty, isUndefined } from 'lodash';
import React, { useState } from 'react';

import { WithCard } from '../WithCard';

/**
 * Widget
 *
 * The logic for the Widget component.
 *
 * @param accessor
 * @param backgroundColor
 * @param button
 * @param canFullScreen
 * @param children
 * @param collapsed
 * @param collapsible
 * @param description
 * @param icon
 * @param isFullscreen
 * @param menu
 * @param panel
 * @param setIsFullscreen
 * @param title
 * @param withCard
 * @returns {Element}
 * @constructor
 */
export function TheWidget({
  accessor = null,
  backgroundColor = 'transparent',
  button = null,
  canFullScreen = false,
  children,
  collapsed = false,
  collapsible = false,
  description = '',
  icon = '',
  isFullscreen = false,
  menu = [],
  panel = undefined,
  setIsFullscreen = () => {},
  title = '',
  withCard = true,
}) {
  const [isCollapsed, setIsCollapsed] = useState(collapsed || false);

  return (
    <motion.div
      animate="open"
      initial={{ opacity: 0 }}
      variants={{
        closed: { opacity: 0 },
        open: { opacity: 1 },
      }}
    >
      <div className={`widget widget-${accessor}`}>
        <WithCard backgroundColor={backgroundColor} withCard={withCard}>
          {panel !== true && (
            <Flex
              alignItems={isEmpty(description) ? 'center' : 'flex-start'}
              className="widget-header"
              justify="space-between"
            >
              <Layout.Group alignItems="top">
                {!isEmpty(icon) && (
                  <div>
                    <IconSymbol color="inherit" icon={icon} variant="outlined" />
                  </div>
                )}
                <Layout.Stack alignItems="left" space={0}>
                  <Heading offset={5}>{title}</Heading>
                  {!isEmpty(description) && (
                    <div>
                      <Text>{description}</Text>
                    </div>
                  )}
                </Layout.Stack>
              </Layout.Group>

              <Layout.Group className="widget-options">
                {!isUndefined(button) && button}
                {!isEmpty(menu) && (
                  <div>
                    <DropdownMenu
                      after={<IconSymbol color="$gray7" icon="more_vert" variant="outlined" />}
                      css={{
                        '.abyss-dropdown-menu-trigger': {
                          backgroundColor: 'none',
                          margin: 0,
                          padding: 0,
                        },
                      }}
                      hideLabel
                      label="options"
                      menuItems={menu || []}
                    />
                  </div>
                )}
                {collapsible === true && (
                  <Button
                    onClick={() => {
                      return setIsCollapsed(!isCollapsed);
                    }}
                    rounded
                    size="$sm"
                    style={{
                      border: '1px solid var(--abyss-colors-interactive1) !important',
                      borderRadius: '4px !important',
                      margin: 0,
                    }}
                    variant="ghost"
                  >
                    {isCollapsed ? (
                      <IconSymbol icon="expand_less" variant="outlined" />
                    ) : (
                      <IconSymbol icon="expand_more" variant="outlined" />
                    )}
                  </Button>
                )}

                {canFullScreen && (
                  <div>
                    <button type="button">
                      <IconSymbol
                        css={{
                          backgroundColor: 'none',
                          cursor: 'pointer',
                          margin: 0,
                          padding: 'var(--abyss-space-sm)',
                        }}
                        icon="fullscreen"
                        onClick={() => {
                          return setIsFullscreen(!isFullscreen);
                        }}
                      />
                    </button>
                  </div>
                )}
              </Layout.Group>
            </Flex>
          )}
          {!isCollapsed && (
            <React.Fragment>
              {panel !== true && <Divider className="widget-divider" />}
              <div className="widget-body">
                <motion.div
                  animate="open"
                  initial={{ opacity: 0 }}
                  variants={{
                    closed: { opacity: 0 },
                    open: { opacity: 1 },
                  }}
                >
                  {children}
                </motion.div>
              </div>
            </React.Fragment>
          )}
        </WithCard>
      </div>
    </motion.div>
  );
}
